import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { financeApi, IncomeRecord } from '../../api/finance';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected' };

export function IncomeRecordsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const recordsQuery = useQuery({ queryKey: ['finance-income-records'], queryFn: () => financeApi.listIncomeRecords() });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts(), enabled: showForm });
  const bankAccountsQuery = useQuery({ queryKey: ['finance-bank-accounts'], queryFn: () => financeApi.listBankAccounts(), enabled: showForm });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-income-records'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createIncomeRecord({
      incomeDate: v.incomeDate, description: v.description, amount: Number(v.amount), incomeAccountId: Number(v.incomeAccountId), bankAccountId: Number(v.bankAccountId),
    }),
    onSuccess: (result) => { showSuccess(`Income record created - ${result.data.incomeReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create income record.'),
  });

  return (
    <div className="page">
      <PageHeader title="Income" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Income Record'}</button>} />
      <p className="muted">Money genuinely received now - donations, interest, asset sale proceeds. A pledge not yet received belongs in Receivables instead.</p>

      {showForm && (
        <section className="card">
          <h2>New Income Record</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Income Date<input type="date" {...register('incomeDate', { required: true })} /></label>
            <label>Description<input {...register('description', { required: true })} /></label>
            <label>Amount<input type="number" step="0.01" {...register('amount', { required: true })} /></label>
            <label>
              Income Account
              <select {...register('incomeAccountId', { required: true })}>
                <option value="">- select -</option>
                {accountsQuery.data?.data.filter((a) => a.posting_allowed && (a.account_type === 'income' || a.account_type === 'other_income')).map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
              </select>
            </label>
            <label>
              Received Into
              <select {...register('bankAccountId', { required: true })}>
                <option value="">- select -</option>
                {bankAccountsQuery.data?.data.filter((b) => b.is_active).map((b) => <option key={b.id} value={b.id}>{b.account_name}</option>)}
              </select>
            </label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Income Records" />
        <div className="panel-body">
        {recordsQuery.data && (
          <DataTable<IncomeRecord>
            rows={recordsQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No income records yet."
            columns={[
              { header: 'Reference', render: (r) => r.income_reference },
              { header: 'Date', render: (r) => r.income_date },
              { header: 'Description', render: (r) => r.description },
              { header: 'Amount', render: (r) => Number(r.amount).toLocaleString() },
              { header: 'Account', render: (r) => r.account_code ? `${r.account_code} - ${r.account_name}` : '-' },
              { header: 'Received Into', render: (r) => r.bank_account_name ?? '-' },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/finance/income/${r.id}`)} /> },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
